import React, { useState, useContext } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
} from '@mui/material';
import { UserContext } from '../UserAndProfile/UserContext';

interface ReferralInviteFormProps {
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const ReferralInviteForm: React.FC<ReferralInviteFormProps> = ({ onSuccess, onError }) => {
  const { sendReferralInvitation, user } = useContext(UserContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    role: 'parent' as 'parent' | 'student' | 'educator',
  });
  const [errors, setErrors] = useState<{
    firstName?: string;
    email?: string;
  }>({});

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Check if trying to refer themselves
    if (user && formData.email.toLowerCase() === user.email.toLowerCase()) {
      newErrors.email = 'You cannot refer yourself';
    }

    // Check if already referred this email
    if (user?.referredUsers?.some(r => r.email.toLowerCase() === formData.email.toLowerCase())) {
      newErrors.email = 'You have already invited this person';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await sendReferralInvitation({
        firstName: formData.firstName.trim(),
        lastName: null,
        email: formData.email.trim().toLowerCase(),
        role: formData.role,
      });

      // Reset form on success
      setFormData({
        firstName: '',
        email: '',
        role: 'parent',
      });
      setErrors({});

      onSuccess(`Invitation sent successfully to ${formData.firstName}!`);
    } catch (error) {
      console.error('Error sending referral invitation:', error);
      onError('Failed to send invitation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mb: 0 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* First Name */}
        <TextField
          label="First Name"
          value={formData.firstName}
          onChange={(e) => handleInputChange('firstName', e.target.value)}
          error={!!errors.firstName}
          helperText={errors.firstName}
          size="small"
          required
          disabled={isSubmitting}
        />

        {/* Email */}
        <TextField
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          error={!!errors.email}
          helperText={errors.email}
          size="small"
          required
          disabled={isSubmitting}
        />

        {/* Role Selection */}
        <FormControl size="small" disabled={isSubmitting}>
          <InputLabel>Who are you inviting?</InputLabel>
          <Select
            value={formData.role}
            label="Who are you inviting?"
            onChange={(e) => handleInputChange('role', e.target.value)}
          >
            <MenuItem value="parent">Parent</MenuItem>
            <MenuItem value="student">Student</MenuItem>
            <MenuItem value="educator">Educator</MenuItem>
          </Select>
        </FormControl>

        {/* Submit Button */}
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          sx={{ mb: 1 }}
        >
          {isSubmitting ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Sending...
            </>
          ) : (
            'Send Invitation'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default ReferralInviteForm; 